import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  VaultIcon,
  CoinsIcon,
  HandHeartIcon,
  PercentIcon,
  ArrowSquareOutIcon,
  ArrowLeftIcon,
  TrendUpIcon,
  CrownIcon,
  GiftIcon,
} from "@phosphor-icons/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import DigestActions from "@/components/DigestActions";

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ymKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function ymLabel(key) {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, {
    month: "short",
    year: "2-digit",
  });
}

export default function AdminRevenue() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    api
      .get("/admin/platform-revenue?limit=500")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  // Month buckets for last 6 months — computed from `items`.
  const monthly = useMemo(() => {
    if (!data?.items) return [];
    const buckets = {};
    for (const it of data.items) {
      const k = ymKey(it.created_at);
      if (!buckets[k]) buckets[k] = { key: k, gross: 0, fee: 0, ref: 0, net: 0, orders: 0 };
      buckets[k].gross += Number(it.gross_sale || 0);
      buckets[k].fee += Number(it.fee_collected || 0);
      buckets[k].ref += Number(it.referral_share_paid || 0);
      buckets[k].net += Number(it.net_to_platform || 0);
      buckets[k].orders += 1;
    }
    // Fill last 6 months even if empty
    const now = new Date();
    const filled = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const k = ymKey(d.toISOString());
      filled.push(buckets[k] || { key: k, gross: 0, fee: 0, ref: 0, net: 0, orders: 0 });
    }
    return filled;
  }, [data]);

  const currentMonthKey = ymKey(new Date().toISOString());
  const mtd = monthly.find((m) => m.key === currentMonthKey) || {
    gross: 0,
    fee: 0,
    ref: 0,
    net: 0,
    orders: 0,
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-12">
          <p className="text-sm text-zinc-500">Loading revenue ledger…</p>
        </main>
        <Footer />
      </div>
    );
  }

  const { totals, items, fee_model, top_sellers = [], tips = { count: 0, total: 0 } } = data;

  // Recharts-compatible month buckets.
  const chartData = monthly.map((m) => ({
    month: ymLabel(m.key),
    Net: Number(m.net.toFixed(2)),
    Fees: Number(m.fee.toFixed(2)),
    Gross: Number(m.gross.toFixed(2)),
  }));
  const sellerChartData = top_sellers.slice(0, 7).map((s) => ({
    name: s.seller_name.length > 14 ? s.seller_name.slice(0, 13) + "…" : s.seller_name,
    Fees: Number(s.fee.toFixed(2)),
  }));

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-revenue-page">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10">
        {/* Header */}
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-[#1D78AB] mb-3"
          data-testid="revenue-back-to-admin"
        >
          <ArrowLeftIcon size={12} /> Back to admin
        </Link>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="pill bg-emerald-500 text-white flex items-center gap-1">
            <VaultIcon size={11} weight="fill" /> OWNER COCKPIT
          </span>
          <span className="pill pill-dark">LIVE</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          Platform revenue.
        </h1>
        <p className="text-zinc-600 mt-3 max-w-2xl">
          The money Bidorah keeps after paying every seller, processor, and referral partner.
          Pulled live from <code className="font-mono text-xs">platform_revenue</code>.
        </p>

        {/* Hero total */}
        <div
          className="mt-8 rounded-3xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 p-6 sm:p-8 text-white relative overflow-hidden"
          data-testid="revenue-hero"
        >
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <p className="text-[10px] font-mono tracking-widest uppercase text-white/80">
            NET TO PLATFORM · LIFETIME
          </p>
          <p
            className="font-display font-black tracking-tighter leading-none mt-2 text-5xl sm:text-7xl"
            data-testid="revenue-hero-net"
          >
            {fmt(totals.net_to_platform)}
          </p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <div>
              <span className="text-white/60">Gross processed · </span>
              <span className="font-semibold">{fmt(totals.gross_sales)}</span>
            </div>
            <div>
              <span className="text-white/60">Orders · </span>
              <span className="font-semibold">{totals.order_count}</span>
            </div>
            <div>
              <span className="text-white/60">Referral leakage · </span>
              <span className="font-semibold">
                {fmt(totals.referral_share_paid)}
                {totals.fees_collected > 0 && (
                  <span className="text-white/60">
                    {" "}
                    ({((totals.referral_share_paid / totals.fees_collected) * 100).toFixed(1)}%)
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="revenue-kpis">
          <Kpi
            label="Gross Sales"
            value={fmt(totals.gross_sales)}
            sub={`${totals.order_count} orders`}
            accent="bg-[#1D78AB]/10 text-[#1D78AB]"
            icon={<CoinsIcon size={18} weight="fill" />}
          />
          <Kpi
            label="Fees Collected"
            value={fmt(totals.fees_collected)}
            sub={`${fee_model.commission_pct}% + ${fee_model.processing_pct}% + $${fee_model.processing_fixed_usd}`}
            accent="bg-amber-400/20 text-amber-700"
            icon={<PercentIcon size={18} weight="fill" />}
          />
          <Kpi
            label="Referral Share"
            value={fmt(totals.referral_share_paid)}
            sub={`paid to Founders (${fee_model.referral_share_pct}%)`}
            accent="bg-red-500/10 text-red-600"
            icon={<HandHeartIcon size={18} weight="fill" />}
          />
          <Kpi
            label="Net To Platform"
            value={fmt(totals.net_to_platform)}
            sub="after every payout"
            accent="bg-emerald-500/15 text-emerald-700"
            icon={<VaultIcon size={18} weight="fill" />}
          />
        </div>

        {/* Weekly Drop Digest controls */}
        <div className="mt-6">
          <DigestActions />
        </div>

        {/* MTD + Fee Model */}
        <div className="mt-6 grid md:grid-cols-[2fr_1fr] gap-3">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="revenue-mtd">
            <div className="flex items-center gap-2 mb-3">
              <TrendUpIcon size={16} weight="fill" className="text-emerald-600" />
              <p className="text-sm font-semibold">Month-to-date</p>
              <span className="text-xs text-zinc-400 font-mono">
                · {ymLabel(currentMonthKey)}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <MtdStat label="Orders" value={mtd.orders} />
              <MtdStat label="Gross" value={fmt(mtd.gross)} />
              <MtdStat label="Fees" value={fmt(mtd.fee)} />
              <MtdStat label="Net" value={fmt(mtd.net)} highlight />
            </div>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5">
            <p className="text-sm font-semibold mb-3">Fee model (live)</p>
            <ul className="text-xs space-y-1.5 text-zinc-700 font-mono">
              <li>
                <span className="text-zinc-400">commission</span> {fee_model.commission_pct}%
              </li>
              <li>
                <span className="text-zinc-400">processing</span> {fee_model.processing_pct}%
              </li>
              <li>
                <span className="text-zinc-400">fixed</span> ${fee_model.processing_fixed_usd}/order
              </li>
              <li className="pt-1 mt-1 border-t border-zinc-200">
                <span className="text-zinc-400">ref share</span> {fee_model.referral_share_pct}%{" "}
                <span className="text-zinc-400">(first order only)</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Monthly chart */}
        <div
          className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5"
          data-testid="revenue-monthly-chart"
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <p className="text-sm font-semibold">Revenue · last 6 months</p>
            <span className="text-[10px] font-mono tracking-widest text-zinc-400 uppercase">
              NET · FEES · GROSS
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradNet" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradFees" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1D78AB" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#1D78AB" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradGross" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="month" fontSize={11} stroke="#71717a" tickLine={false} />
                <YAxis fontSize={11} stroke="#71717a" tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7", fontSize: 12 }}
                  formatter={(v) => [`$${Number(v).toLocaleString()}`, ""]}
                />
                <Area type="monotone" dataKey="Gross" stroke="#f59e0b" strokeWidth={2} fill="url(#gradGross)" />
                <Area type="monotone" dataKey="Fees" stroke="#1D78AB" strokeWidth={2} fill="url(#gradFees)" />
                <Area type="monotone" dataKey="Net" stroke="#10b981" strokeWidth={2.5} fill="url(#gradNet)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top sellers + tips */}
        <div className="mt-6 grid lg:grid-cols-[1.3fr_1fr] gap-3">
          <div
            className="rounded-2xl border border-zinc-200 bg-white p-5"
            data-testid="revenue-top-sellers"
          >
            <div className="flex items-center gap-2 mb-4">
              <CrownIcon size={16} weight="fill" className="text-amber-500" />
              <p className="text-sm font-semibold">Top sellers by fees generated</p>
            </div>
            {sellerChartData.length === 0 ? (
              <p className="text-xs text-zinc-500 py-12 text-center">
                No paid orders yet. The leaderboard lights up after the first checkout.
              </p>
            ) : (
              <>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sellerChartData} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
                      <XAxis type="number" fontSize={11} stroke="#71717a" tickFormatter={(v) => `$${v}`} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        fontSize={11}
                        stroke="#71717a"
                        width={90}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7", fontSize: 12 }}
                        formatter={(v) => [`$${Number(v).toLocaleString()}`, "Fees"]}
                      />
                      <Bar dataKey="Fees" fill="#1D78AB" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <ul className="mt-3 space-y-1 text-xs text-zinc-600">
                  {top_sellers.slice(0, 5).map((s, i) => (
                    <li key={s.seller_id} className="flex items-center gap-2">
                      <span className="font-mono text-zinc-400 w-4">{i + 1}.</span>
                      <span className="flex-1 truncate font-semibold text-zinc-800">
                        {s.seller_name}
                      </span>
                      <span className="font-mono text-zinc-500 whitespace-nowrap">
                        {s.orders} order{s.orders === 1 ? "" : "s"}
                      </span>
                      <span className="font-mono font-semibold text-[#1D78AB] w-20 text-right">
                        {fmt(s.fee)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div
            className="rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-white p-5 flex flex-col"
            data-testid="revenue-tips"
          >
            <div className="flex items-center gap-2 mb-3">
              <GiftIcon size={16} weight="fill" className="text-amber-600" />
              <p className="text-sm font-semibold">Creator tips</p>
            </div>
            <p className="text-[10px] font-mono tracking-widest text-amber-700 uppercase">
              Lifetime tipped
            </p>
            <p className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-1 text-zinc-900">
              {fmt(tips.total)}
            </p>
            <p className="text-xs text-zinc-600 mt-1">
              {tips.count} tip{tips.count === 1 ? "" : "s"} · 100% passes to creators (no platform cut)
            </p>
            <div className="mt-auto pt-4 text-[11px] text-zinc-500">
              Tips flow directly to sellers via Stripe — Bidorah does not take a cut.
            </div>
          </div>
        </div>

        {/* Recent rows */}
        <div className="mt-6 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
          <div className="p-5 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold">Recent revenue rows</p>
            <span className="text-xs text-zinc-400 font-mono">
              {items.length} of {totals.order_count}
            </span>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500">
              No paid orders yet — revenue will land here the moment the first Stripe / PayPal
              checkout clears.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[10px] font-mono tracking-widest text-zinc-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">When</th>
                    <th className="px-4 py-3 text-left font-semibold">Order</th>
                    <th className="px-4 py-3 text-right font-semibold">Gross</th>
                    <th className="px-4 py-3 text-right font-semibold">Fee</th>
                    <th className="px-4 py-3 text-right font-semibold">Ref Paid</th>
                    <th className="px-4 py-3 text-right font-semibold">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {items.slice(0, 40).map((it) => (
                    <tr
                      key={it.id}
                      className="border-t border-zinc-100 hover:bg-zinc-50"
                      data-testid={`revenue-row-${it.id}`}
                    >
                      <td className="px-4 py-3 text-zinc-600 font-mono text-xs whitespace-nowrap">
                        {new Date(it.created_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/orders/${it.order_id}`}
                          className="font-mono text-xs text-[#1D78AB] hover:underline inline-flex items-center gap-1"
                        >
                          {it.order_id.slice(-8).toUpperCase()}
                          <ArrowSquareOutIcon size={10} />
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{fmt(it.gross_sale)}</td>
                      <td className="px-4 py-3 text-right font-mono text-amber-700">
                        {fmt(it.fee_collected)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-red-600">
                        {Number(it.referral_share_paid) > 0 ? `−${fmt(it.referral_share_paid)}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600">
                        {fmt(it.net_to_platform)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Kpi({ label, value, sub, accent, icon }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">{label}</p>
        <span className={`h-7 w-7 rounded-lg grid place-items-center ${accent}`}>{icon}</span>
      </div>
      <p className="font-display font-black text-xl sm:text-2xl tracking-tight mt-2">{value}</p>
      {sub && <p className="text-[11px] text-zinc-500 mt-1 leading-tight">{sub}</p>}
    </div>
  );
}

function MtdStat({ label, value, highlight }) {
  return (
    <div>
      <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">{label}</p>
      <p
        className={`font-display font-black text-lg sm:text-xl mt-1 ${
          highlight ? "text-emerald-600" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}
